import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { IndianRupee, Plus, Check, Clock, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState, useMemo, useCallback, useEffect } from "react";
import {
  useNavigate,
  useLocation,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { HaafPaymentsTable } from "@/pages/dashboard/admin/haaf-payments/components/haaf-payments-table";
import { AddHaafPaymentForm } from "@/pages/dashboard/admin/haaf-payments/components/add-haaf-payment-form";
import { HaafReceiptModal } from "@/components/haaf-payment/HaafReceiptModal";
import {
  useHaafPayments,
  useVerifyHaafPayment,
  useUnverifyHaafPayment,
  useDeleteHaafPayment,
  useHaafPaymentBatchStats,
  useHaafPaymentChapterStats,
} from "@/api/haaf-payments";
// import { useAuth } from "@/contexts/AuthContext"; // Available if needed
import { Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { isWithinInterval, startOfDay, endOfDay } from "date-fns";
import { currencies } from "@/lib/constants";
import { useDebounce } from "@/hooks/useDebounce";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

const ITEMS_PER_LOAD = 20;

function HafPaymentsList() {
  const navigate = useNavigate();
  const location = useLocation();
  // const { user } = useAuth(); // Available if needed for user-specific data
  const [open, setOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [displayedItemsCount, setDisplayedItemsCount] =
    useState(ITEMS_PER_LOAD);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Determine active tab from URL
  const currentPath = location.pathname;
  const activeTab = currentPath.endsWith("/statistics")
    ? "statistics"
    : "payments";

  // Collector has full access to all payments (admin-level access)
  const { data: payments, isLoading } = useHaafPayments();
  const { data: batchStats, isLoading: isBatchStatsLoading } =
    useHaafPaymentBatchStats();
  const { data: chapterStats, isLoading: isChapterStatsLoading } =
    useHaafPaymentChapterStats();

  // Handle tab changes via URL navigation
  const handleTabChange = useCallback(
    (newTab) => {
      const basePath = "/haaf-collector/haf-payments";
      const newPath =
        newTab === "statistics" ? `${basePath}/statistics` : basePath;
      navigate(newPath);
    },
    [navigate]
  );

  // Full admin permissions for collector
  const verifyMutation = useVerifyHaafPayment();
  const unverifyMutation = useUnverifyHaafPayment();
  const deleteMutation = useDeleteHaafPayment();
  // const updateMutation = useUpdateHaafPayment(); // Available if needed
  const [editPayment, setEditPayment] = useState(null);

  const handleVerificationToggle = useCallback(
    (payment) => {
      if (payment.verified) {
        unverifyMutation.mutate(payment._id);
      } else {
        verifyMutation.mutate(payment._id);
      }
    },
    [verifyMutation, unverifyMutation]
  );

  const handleDelete = useCallback(
    (paymentId) => {
      if (window.confirm("Are you sure you want to delete this payment?")) {
        deleteMutation.mutate(paymentId);
      }
    },
    [deleteMutation]
  );

  const handleEdit = useCallback((payment) => {
    setEditPayment(payment);
  }, []);

  const handleDownloadReceipt = useCallback((payment) => {
    setSelectedReceipt(payment);
    setReceiptModalOpen(true);
  }, []);

  const handleReceiptClose = useCallback(() => {
    setReceiptModalOpen(false);
    setSelectedReceipt(null);
  }, []);

  // Get unique batch values for filter
  const batches = useMemo(() => {
    if (!payments) return [];
    const batchSet = new Set(payments.map((p) => p.batch).filter(Boolean));
    return Array.from(batchSet).sort();
  }, [payments]);

  // Filter payments to show only collector's own payments
  // Note: In a real app, this would be handled on the backend
  const { filteredPayments, displayedPayments, hasMore } = useMemo(() => {
    if (!payments)
      return { filteredPayments: [], displayedPayments: [], hasMore: false };

    let filtered = payments;

    // Apply search filter
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (payment) =>
          payment.paymentId?.toLowerCase().includes(query) ||
          payment.donorName?.toLowerCase().includes(query) ||
          payment.chapterName?.toLowerCase().includes(query)
      );
    }

    // Apply batch filter
    if (selectedBatch !== "all") {
      filtered = filtered.filter((payment) => payment.batch === selectedBatch);
    }

    // Apply date range filter
    if (dateRange.from || dateRange.to) {
      filtered = filtered.filter((payment) => {
        const paymentDate = new Date(payment.createdAt);
        const from = dateRange.from ? startOfDay(dateRange.from) : null;
        const to = dateRange.to ? endOfDay(dateRange.to) : null;

        if (from && to) {
          return isWithinInterval(paymentDate, { start: from, end: to });
        } else if (from) {
          return paymentDate >= from;
        } else if (to) {
          return paymentDate <= to;
        }
        return true;
      });
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });

    const displayed = sorted.slice(0, displayedItemsCount);
    const hasMoreItems = sorted.length > displayedItemsCount;

    return {
      filteredPayments: sorted,
      displayedPayments: displayed,
      hasMore: hasMoreItems,
    };
  }, [
    payments,
    debouncedSearchQuery,
    selectedBatch,
    dateRange,
    sortOrder,
    displayedItemsCount,
  ]);

  // Load more items
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    setTimeout(() => {
      setDisplayedItemsCount((prev) => prev + ITEMS_PER_LOAD);
      setIsLoadingMore(false);
    }, 500);
  }, [isLoadingMore, hasMore]);

  // Window scroll event handler
  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop !==
        document.documentElement.offsetHeight ||
      isLoadingMore ||
      !hasMore
    ) {
      return;
    }
    loadMore();
  }, [hasMore, isLoadingMore, loadMore]);

  // Add window scroll listener
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Reset displayed items when filters change
  const handleBatchChange = useCallback((newBatch) => {
    setSelectedBatch(newBatch);
    setDisplayedItemsCount(ITEMS_PER_LOAD);
  }, []);

  const handleSortChange = useCallback((newSort) => {
    setSortOrder(newSort);
    setDisplayedItemsCount(ITEMS_PER_LOAD);
  }, []);

  const handleDateRangeChange = useCallback((field, date) => {
    setDateRange((prev) => ({ ...prev, [field]: date }));
    setDisplayedItemsCount(ITEMS_PER_LOAD);
  }, []);

  // Reset when search changes
  useEffect(() => {
    setDisplayedItemsCount(ITEMS_PER_LOAD);
  }, [debouncedSearchQuery]);

  // Calculate stats with useMemo for better performance
  const paymentsStats = useMemo(
    () => [
      {
        title: "Total Collections",
        value:
          filteredPayments?.reduce((acc, curr) => {
            const currency = curr.currency || "QAR";
            acc[currency] = (acc[currency] || 0) + (curr.amount || 0);
            return acc;
          }, {}) || {},
        icon: IndianRupee,
        description: "Total amount collected",
        isCurrencyGroup: true,
      },
      {
        title: "Verified Payments",
        value: filteredPayments?.filter((p) => p.verified).length || 0,
        icon: Check,
        description: "Number of verified payments",
      },
      {
        title: "Pending Verification",
        value: filteredPayments?.filter((p) => !p.verified).length || 0,
        icon: Clock,
        description: "Payments awaiting verification",
      },
    ],
    [filteredPayments]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            HAF Payments Management
          </h2>
          <p className="text-muted-foreground">
            Manage, verify, and track all HAF payment collections
          </p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Payment
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="payments">All Payments</TabsTrigger>
          <TabsTrigger value="statistics">
            <BarChart3 className="mr-2 h-4 w-4" />
            Statistics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="payments" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            {paymentsStats.map((stat, index) => (
              <Card key={index}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {stat.title}
                  </CardTitle>
                  <stat.icon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  {stat.isCurrencyGroup ? (
                    <div className="space-y-1">
                      {Object.entries(stat.value).map(([currency, amount]) => (
                        <div key={currency} className="text-xl font-bold">
                          {currencies[currency]?.symbol || currency}{" "}
                          {amount.toLocaleString()}
                        </div>
                      ))}
                      {Object.keys(stat.value).length === 0 && (
                        <div className="text-xl font-bold">QR 0</div>
                      )}
                    </div>
                  ) : (
                    <div className="text-2xl font-bold">{stat.value}</div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle>Filters & Search</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-4">
                <Select value={selectedBatch} onValueChange={handleBatchChange}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by batch" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Batches</SelectItem>
                    {batches.map((batch) => (
                      <SelectItem key={batch} value={batch}>
                        Batch {batch}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={sortOrder} onValueChange={handleSortChange}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Sort by date" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desc">Newest First</SelectItem>
                    <SelectItem value="asc">Oldest First</SelectItem>
                  </SelectContent>
                </Select>
                <DatePicker
                  date={dateRange.from}
                  onDateChange={(date) => handleDateRangeChange("from", date)}
                  placeholder="From date"
                />
                <DatePicker
                  date={dateRange.to}
                  onDateChange={(date) => handleDateRangeChange("to", date)}
                  placeholder="To date"
                />
              </div>
            </CardContent>
          </Card>

          {/* Payments Table */}
          <HaafPaymentsTable
            data={displayedPayments}
            onVerificationToggle={handleVerificationToggle} // Full verification access for collectors
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            onEdit={handleEdit}
            onDelete={handleDelete} // Full delete access for collectors
            onDownloadReceipt={handleDownloadReceipt}
            showActions={true}
            hideVerificationToggle={false} // Show verification controls for collectors
            isLoading={isLoadingMore}
          />

          {/* Load More Button */}
          {hasMore && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={loadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  `Load More (${
                    filteredPayments.length - displayedItemsCount
                  } remaining)`
                )}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="statistics" className="space-y-6">
          {/* Batch Statistics */}
          <Card>
            <CardHeader>
              <CardTitle>My Collection Statistics by Batch</CardTitle>
            </CardHeader>
            <CardContent>
              {isBatchStatsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <ChartContainer
                  config={{
                    amount: {
                      label: "Amount",
                      color: "hsl(var(--chart-1))",
                    },
                  }}
                  className="h-[300px]"
                >
                  <BarChart data={batchStats || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="batch" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="totalAmount" fill="var(--color-amount)" />
                  </BarChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>

          {/* Chapter Statistics */}
          <Card>
            <CardHeader>
              <CardTitle>My Collection Statistics by Chapter</CardTitle>
            </CardHeader>
            <CardContent>
              {isChapterStatsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <ChartContainer
                  config={{
                    amount: {
                      label: "Amount",
                      color: "hsl(var(--chart-2))",
                    },
                  }}
                  className="h-[300px]"
                >
                  <BarChart data={chapterStats || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="chapterName" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="totalAmount" fill="var(--color-amount)" />
                  </BarChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Payment Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New HAF Payment</DialogTitle>
            <DialogDescription>
              Record a new HAF payment collection.
            </DialogDescription>
          </DialogHeader>
          <AddHaafPaymentForm
            onClose={() => setOpen(false)}
            editPayment={editPayment}
            onEditComplete={() => setEditPayment(null)}
          />
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <HaafReceiptModal
        isOpen={receiptModalOpen}
        onClose={handleReceiptClose}
        receipt={selectedReceipt}
      />
    </div>
  );
}

export default function HafPayments() {
  return (
    <Routes>
      <Route path="/" element={<HafPaymentsList />} />
      <Route
        path="*"
        element={<Navigate to="/haaf-collector/haf-payments" replace />}
      />
    </Routes>
  );
}
